import PlaystoreBadge from '../../assets/playstore-badge.svg'
import IosBadge from '../../assets/appstore-badge.svg'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog'
import { useMemo } from 'react'
import { getDeferredInstallUrlForCurrentPage } from '../../utils/mobileApp'
import { trackEvent } from '../../utils/analytics'
import { userUtils } from '../../utils/auth'
import { generateId } from '../../utils/idGenerator'

type AppStorePopUpProps = {
    open: boolean
    onOpenChange: (next: boolean) => void
}

export default function AppStorePopUp({ open, onOpenChange }: AppStorePopUpProps) {
    const androidInstallUrl = useMemo(() => getDeferredInstallUrlForCurrentPage('android', { itmSource: 'store' }), [])
    const iosInstallUrl = useMemo(() => getDeferredInstallUrlForCurrentPage('ios', { itmSource: 'store' }), [])

    // Retrieve user or provide a stable temporary session id
    const getUserOrTempSession = () => {
        const user = userUtils.getUser();
        if (user) return { user };
        const key = 'temp_session_id';
        let sid = localStorage.getItem(key);
        if (!sid) {
            sid = generateId();
            localStorage.setItem(key, sid);
        }
        return { tempSessionId: sid };
    }

    const handleDownloadClick = async (platform: 'android' | 'ios') => {
        const { user, tempSessionId } = getUserOrTempSession();
        await trackEvent('app download clicked', {
            platform,
            source: 'store_popup',
            ...(user ? { userId: user.id, email: user.email, name: user.name } : { tempSessionId }),
        });
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent showCloseButton={false} className="p-0 gap-0 border-0 bg-transparent max-w-[760px] w-[90vw]">
                <DialogTitle className="sr-only">Download Our App</DialogTitle>
                <DialogDescription className="sr-only">Download the Auqli app to shop the way you love</DialogDescription>
                <div className="relative w-full max-w-[734px] h-auto min-h-[400px] rounded-[1.25rem] bg-[#16783A] text-white flex items-center justify-center px-6 py-8">
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={() => onOpenChange(false)}
                        className="absolute right-4 top-4 h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-white/10"
                    >
                        <svg width="20" height="20" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M26.25 8.75L8.75 26.25M8.75 8.75L26.25 26.25" stroke="white" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <div className="inline-flex flex-col items-center gap-6">
                        <div className="flex flex-col items-center">
                            <div className="text-white text-center font-lexend text-[2.5rem] md:text-[3.125rem] font-medium leading-tight capitalize">
                                Download Our App
                            </div>
                            <div className="text-white text-center font-lexend text-[1.1rem] md:text-[1.25rem] font-light leading-[140%] capitalize max-w-[90%] md:max-w-[522px] mt-4 md:mt-6">
                                Shop the way you love with Auqli! Download our FREE app today and experience thrill!
                            </div>
                        </div>
                        <div className="flex items-center gap-4 md:gap-6 mt-2 md:mt-4">
                            <a onClick={() => handleDownloadClick('android')} href={androidInstallUrl} aria-label="Get it on Google Play" className="h-[49px] md:h-[70px] w-fit md:w-[236px] inline-flex items-center justify-center">
                                <img src={PlaystoreBadge} alt="Get it on Google Play" className="h-full w-auto" />
                            </a>
                            <a onClick={() => handleDownloadClick('ios')} href={iosInstallUrl} aria-label="Download on the App Store" className="h-[49px] md:h-[70px] w-[148px] md:w-[210px] inline-flex items-center justify-center">
                                <img src={IosBadge} alt="Download on the App Store" className="h-full w-auto" />
                            </a>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}